import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { toast } from 'react-toastify';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Edit as EditIcon,
  FileUp,
  Mail,
  MoreVertical,
  Phone,
  Plus,
  RefreshCw,
  Search as SearchIcon,
  ShieldCheck,
  HelpCircle,
  Trash2 as DeleteIcon,
  Upload
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%'
}));

const StatWidget = ({ title, value, icon: Icon, color, subtitle, subtitleColor, onClick }) => (
  <Paper
    sx={{
      p: 1.5,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      overflow: 'hidden',
      minWidth: 0,
      cursor: onClick ? 'pointer' : 'default',
      userSelect: 'none',
      '&:hover': onClick ? { bgcolor: 'rgba(0,0,0,0.02)' } : undefined
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.25,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color: color,
        mr: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={20} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography
          variant="caption"
          noWrap
          sx={{ mt: 0.25, color: subtitleColor || 'text.secondary', display: 'block', maxWidth: '100%' }}
          title={subtitle}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

const formatNumber = (value) => Number(value || 0).toLocaleString('it-IT');

const formatPercent1 = (value) =>
  Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatDateIt = (value) => {
  if (!value) return '-';
  const s = String(value);
  const ymd = s.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return String(value);
  const pad2 = (v) => String(v).padStart(2, '0');
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const isEmailLike = (value) => {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const diffDaysFromToday = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - x.getTime()) / (24 * 60 * 60 * 1000));
};

const getCurrentMonthFromToTodayYmd = () => {
  const now = new Date();
  const year = now.getFullYear();
  const monthIndex = now.getMonth();
  const first = new Date(year, monthIndex, 1);
  const pad2 = (v) => String(v).padStart(2, '0');
  const from = `${first.getFullYear()}-${pad2(first.getMonth() + 1)}-${pad2(first.getDate())}`;
  const to = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  return { from, to };
};

const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
  ]
};

const quillFormats = [
  'font',
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'script',
  'align',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'code-block',
  'link',
  'image'
];

const StatoChip = ({ value }) => {
  const v = String(value || '').toLowerCase();
  if (v === 'inviato') return <Chip size="small" label="Inviato" color="success" />;
  if (v === 'errore_invio') return <Chip size="small" label="Errore" color="error" />;
  return <Chip size="small" label="Da inviare" color="warning" variant="outlined" />;
};

const CanaleChip = ({ value }) => {
  const v = String(value || '').toLowerCase();
  if (v === 'whatsapp') return <Chip size="small" label="WhatsApp" color="success" variant="outlined" />;
  if (v === 'pec') return <Chip size="small" label="PEC" color="info" variant="outlined" />;
  return <Chip size="small" label="Email" color="default" variant="outlined" />;
};

const defaultForm = {
  tipo_canale: 'email',
  stato: 'da_inviare',
  cliente_record_id: '',
  cognome: '',
  nome: '',
  codice_fiscale: '',
  partita_iva: '',
  email: '',
  telefono: '',
  modulistica_id: '',
  modello: '',
  data_programmata_invio: '',
  data_scadenza: ''
};

const errorReasonLabel = (row) => {
  const reason = String(row?.errore_invio_motivo || '').trim().toLowerCase();
  if (reason === 'invalid_email') return 'Email non valida';
  if (reason === 'empty_content') return 'Contenuto vuoto';
  if (reason === 'send_error') {
    const detail = String(row?.errore_invio_dettaglio || '').toLowerCase();
    if (detail.includes('policy violation')) return 'SMTP policy violation';
    if (detail.includes('invalid greeting response=421')) return 'SMTP policy violation (421)';
    return 'Errore invio (SMTP)';
  }
  if (row?.errore_invio_dettaglio) return String(row.errore_invio_dettaglio);
  return '';
};

const getInvioEmailDecision = (row, legalStaleDays, ignoreLegalThresholdOnSend) => {
  const stato = String(row?.stato || '');
  const canale = String(row?.tipo_canale || '');
  if (stato !== 'da_inviare') {
    return { status: 'skip', label: 'Saltato: stato diverso da "da inviare"' };
  }
  if (canale !== 'email') {
    return { status: 'skip', label: 'Saltato: canale diverso da Email' };
  }

  if (!ignoreLegalThresholdOnSend) {
    const threshold = Number(legalStaleDays);
    if (Number.isFinite(threshold) && threshold >= 0) {
      const days = diffDaysFromToday(row?.last_legal_note_date);
      const isStale = days == null ? true : days > threshold;
      if (isStale) {
        return { status: 'skip', label: `Saltato: ultima nota legale oltre ${Math.floor(threshold)} giorni o assente` };
      }
    }
  }

  if (!isEmailLike(row?.email)) {
    return { status: 'blocked', label: 'Non inviabile: email non valida' };
  }
  const content = String(row?.modello || '').trim();
  if (!content) {
    return { status: 'blocked', label: 'Non inviabile: contenuto vuoto' };
  }
  return { status: 'send', label: 'Verrà inviata Email' };
};

export default function ScadenzarioInvioPromemoria() {
  const { addJob, jobs } = useJob();
  const handledBulkEmailRefreshRef = useRef(new Set());
  const handledBulkEmailResultRef = useRef(new Set());
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [meta, setMeta] = useState({ modulistica: [], creatori: [], email_accounts: [], canali: [], stati: [] });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('inviato_at');
  const [orderDirection, setOrderDirection] = useState('desc');

  const [search, setSearch] = useState('');
  const [legalStaleDays, setLegalStaleDays] = useState(30);
  const [onlyLegalStale, setOnlyLegalStale] = useState(false);
  const [filters, setFilters] = useState(() => {
    const { from, to } = getCurrentMonthFromToTodayYmd();
    return {
      stato: 'da_inviare',
      tipo_canale: '',
      creato_da_utente: '',
      date_from: from,
      date_to: to,
      inviato_from: '',
      inviato_to: ''
    };
  });
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [saveLoading, setSaveLoading] = useState(false);
  const [modulisticaContentLoading, setModulisticaContentLoading] = useState(false);

  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerInput, setCustomerInput] = useState('');
  const [customerSelected, setCustomerSelected] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [previewHtml, setPreviewHtml] = useState(false);
  const [csvInfoOpen, setCsvInfoOpen] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const csvInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvDefaultChannel, setCsvDefaultChannel] = useState('email');
  const [csvDefaultModel, setCsvDefaultModel] = useState(null);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  const [csvImportErrors, setCsvImportErrors] = useState([]);
  const [dialogTab, setDialogTab] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const selectedIdsSet = useMemo(() => new Set(selectedIds.map((v) => Number(v))), [selectedIds]);

  const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState(null);
  const bulkMenuOpen = Boolean(bulkMenuAnchorEl);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkScope, setBulkScope] = useState('selected');
  const [bulkStatus, setBulkStatus] = useState('da_inviare');
  const [bulkEmailAccountId, setBulkEmailAccountId] = useState('');
  const [bulkEmailSubject, setBulkEmailSubject] = useState('Promemoria');
  const [bulkApplyLegalThreshold, setBulkApplyLegalThreshold] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkEmailResultOpen, setBulkEmailResultOpen] = useState(false);
  const [bulkEmailResult, setBulkEmailResult] = useState(null);

  const clearSelection = () => setSelectedIds([]);

  const toggleSelectedId = (id) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return;
    setSelectedIds((prev) => {
      const set = new Set(prev.map((v) => Number(v)));
      if (set.has(numericId)) set.delete(numericId);
      else set.add(numericId);
      return Array.from(set);
    });
  };

  const toggleAllVisible = () => {
    const visibleIds = (rows || []).map((r) => Number(r.id)).filter((v) => Number.isFinite(v));
    if (!visibleIds.length) return;
    const visibleSet = new Set(visibleIds);
    const allSelected = visibleIds.every((id) => selectedIdsSet.has(id));
    setSelectedIds((prev) => {
      const set = new Set(prev.map((v) => Number(v)));
      if (allSelected) {
        for (const id of visibleSet) set.delete(id);
      } else {
        for (const id of visibleSet) set.add(id);
      }
      return Array.from(set);
    });
  };

  const handleOpenCsvDialog = () => {
    setCsvInfoOpen(true);
    setCsvUploadOpen(false);
    setCsvFile(null);
    setCsvDefaultChannel('email');
    setCsvDefaultModel(null);
    setCsvImportResult(null);
    setCsvImportErrors([]);
  };

  const handleCloseCsvInfoDialog = () => {
    setCsvInfoOpen(false);
  };

  const handleContinueCsvInfo = () => {
    setCsvInfoOpen(false);
    setCsvUploadOpen(true);
  };

  const handleCloseCsvUploadDialog = () => {
    if (csvImportLoading) return;
    setCsvUploadOpen(false);
  };

  const handleCsvPick = () => {
    if (csvImportLoading) return;
    csvInputRef.current?.click?.();
  };

  const handleCsvFileChange = (e) => {
    const file = e.target?.files?.[0] || null;
    setCsvFile(file);
    setCsvImportResult(null);
    setCsvImportErrors([]);
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error('Seleziona un file CSV');
      return;
    }
    if (!csvDefaultModel?.id) {
      toast.error('Seleziona un modello di default');
      return;
    }
    setCsvImportLoading(true);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      form.append('default_tipo_canale', String(csvDefaultChannel || 'email'));
      form.append('default_modulistica_id', String(csvDefaultModel.id));
      const res = await api.post('/invii-modulistica/import-csv', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data || null;
      setCsvImportResult(data);
      setCsvImportErrors(Array.isArray(data?.errors) ? data.errors : []);
      toast.success(data?.message || 'Import completato');
      await fetchRows();
    } catch (e) {
      const message = e.response?.data?.message || 'Errore durante importazione CSV';
      toast.error(message);
    } finally {
      setCsvImportLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const res = await api.get('/invii-modulistica/meta');
      setMeta(res.data || { modulistica: [], email_accounts: [], canali: [], stati: [] });
    } catch (e) {
      console.error('Error fetching invii modulistica meta:', e);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get('/invii-modulistica', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          orderBy,
          orderDirection,
          only_legal_stale: onlyLegalStale ? 1 : 0,
          legal_stale_days: legalStaleDays,
          ...filters
        }
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.meta?.total || 0));
      setStats(res.data?.stats || null);
      setError('');
    } catch (e) {
      console.error('Error fetching invii modulistica rows:', e);
      setError('Errore nel caricamento degli invii');
      toast.error('Errore nel caricamento degli invii');
    } finally {
      setLoading(false);
    }
  };

  const openBulkMenu = (e) => {
    setBulkMenuAnchorEl(e.currentTarget);
  };

  const closeBulkMenu = () => {
    setBulkMenuAnchorEl(null);
  };

  const openBulkDialog = (action) => {
    closeBulkMenu();
    setBulkAction(action);
    setBulkStatus('da_inviare');
    setBulkScope(selectedIds.length > 0 ? 'selected' : 'all');
    setBulkEmailSubject('Promemoria');
    setBulkEmailAccountId(emailAccountOptions?.[0]?.id ? String(emailAccountOptions[0].id) : '');
    setBulkApplyLegalThreshold(false);
    setBulkDialogOpen(true);
  };

  const closeBulkDialog = () => {
    if (bulkLoading) return;
    setBulkDialogOpen(false);
  };

  const runBulkAction = async () => {
    if (bulkLoading) return;
    const scope = bulkScope;
    const ids = scope === 'selected' ? selectedIds.map((v) => Number(v)).filter((v) => Number.isFinite(v)) : [];
    if (scope === 'selected' && ids.length === 0) {
      toast.error('Seleziona almeno una riga');
      return;
    }

    const payload = {
      scope,
      ids,
      filters: { ...filters, search }
    };

    try {
      setBulkLoading(true);
      if (bulkAction === 'send_email') {
        if (!String(bulkEmailAccountId || '').trim()) {
          toast.error('Seleziona un account email mittente');
          setBulkLoading(false);
          return;
        }
        if (!String(bulkEmailSubject || '').trim()) {
          toast.error('Inserisci l’oggetto');
          setBulkLoading(false);
          return;
        }
        const res = await api.post('/invii-modulistica/bulk/send-email', {
          ...payload,
          legal_stale_days: legalStaleDays,
          ignore_legal_threshold: !bulkApplyLegalThreshold,
          email_account_id: String(bulkEmailAccountId),
          subject: String(bulkEmailSubject)
        });
        const jobId = res.data?.jobId;
        if (!jobId) {
          throw new Error('jobId mancante');
        }
        addJob(jobId, 'invii_modulistica_bulk_send_email', { silent: true });
        toast.info('Invio in corso... l’esito apparirà nelle notifiche (campanella).', {
          position: 'top-right',
          autoClose: 5000
        });
      } else if (bulkAction === 'change_status') {
        const res = await api.post('/invii-modulistica/bulk/update-stato', { ...payload, stato: bulkStatus });
        toast.success(res.data?.message || 'Stato aggiornato');
      } else if (bulkAction === 'delete') {
        const res = await api.post('/invii-modulistica/bulk/delete', payload);
        toast.success(res.data?.message || 'Cancellazione completata');
      }
      clearSelection();
      setBulkDialogOpen(false);
      if (bulkAction !== 'send_email') {
        await fetchRows();
      }
    } catch (e) {
      const message = e.response?.data?.message || 'Errore durante l’azione massiva';
      toast.error(message);
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    const completedJobs = (jobs || []).filter(
      (j) => j?.type === 'invii_modulistica_bulk_send_email' && j?.status === 'completed'
    );
    let shouldRefresh = false;
    completedJobs.forEach((j) => {
      const id = String(j?.id || '');
      if (!id) return;
      if (!handledBulkEmailRefreshRef.current.has(id)) {
        handledBulkEmailRefreshRef.current.add(id);
        shouldRefresh = true;
      }
      if (!handledBulkEmailResultRef.current.has(id) && j?.result) {
        handledBulkEmailResultRef.current.add(id);
        setBulkEmailResult(j.result);
        setBulkEmailResultOpen(true);
      }
    });
    if (shouldRefresh) {
      fetchRows();
    }
  }, [jobs]);

  const downloadBulkEmailErrorsCsv = () => {
    const errors = Array.isArray(bulkEmailResult?.errors) ? bulkEmailResult.errors : [];
    if (!errors.length) return;

    const escape = (value) => {
      const s = String(value ?? '');
      if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const header = ['id', 'messaggio'];
    const rowsCsv = errors.map((e) => [e?.id ?? '', e?.message ?? '']);
    const csv = [header, ...rowsCsv].map((row) => row.map(escape).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invio_promemoria_errori_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    fetchRows();
  }, [page, rowsPerPage, orderBy, orderDirection, search, filters, onlyLegalStale, legalStaleDays]);

  const modulisticaOptions = useMemo(() => {
    return [...(meta.modulistica || [])]
      .map((m) => ({ id: String(m.id), label: m.nome_modello || `Modello ${m.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [meta.modulistica]);

  const emailAccountOptions = useMemo(() => {
    return [...(meta.email_accounts || [])]
      .map((a) => ({
        id: String(a.id),
        label: `${a.name || a.email || `Account ${a.id}`}${a.email ? ` <${a.email}>` : ''}`.trim()
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [meta.email_accounts]);

  const creatoriOptions = useMemo(() => {
    return [...(meta.creatori || [])]
      .map((u) => ({ id: String(u.id), label: u.name || `Utente ${u.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [meta.creatori]);

  const selectedModulisticaFilter =
    modulisticaOptions.find((m) => String(m.id) === String(filters.modulistica_id || '')) || null;
  const selectedModulisticaForm =
    modulisticaOptions.find((m) => String(m.id) === String(formData.modulistica_id || '')) || null;

  const activeFilterChips = [];
  const searchLabel = String(search || '').trim();
  if (searchLabel) {
    activeFilterChips.push({
      key: 'search',
      label: `Ricerca: ${searchLabel}`,
      onDelete: () => {
        setSearch('');
        setPage(0);
      }
    });
  }
  if (filters.stato) {
    const statoLabel =
      filters.stato === 'da_inviare'
        ? 'Da inviare'
        : filters.stato === 'inviato'
          ? 'Inviato'
          : filters.stato === 'errore_invio'
            ? 'Errore invio'
            : filters.stato;
    activeFilterChips.push({
      key: 'stato',
      label: `Stato: ${statoLabel}`,
      onDelete: () => {
        setFilters((prev) => ({ ...prev, stato: '' }));
        setPage(0);
      }
    });
  }
  if (filters.tipo_canale) {
    activeFilterChips.push({
      key: 'tipo_canale',
      label: `Canale: ${filters.tipo_canale.toUpperCase()}`,
      onDelete: () => {
        setFilters((prev) => ({ ...prev, tipo_canale: '' }));
        setPage(0);
      }
    });
  }
  if (filters.creato_da_utente) {
    const userLabel = creatoriOptions.find((u) => String(u.id) === String(filters.creato_da_utente))?.label || filters.creato_da_utente;
    activeFilterChips.push({
      key: 'creato_da_utente',
      label: `Utente: ${userLabel}`,
      onDelete: () => {
        setFilters((prev) => ({ ...prev, creato_da_utente: '' }));
        setPage(0);
      }
    });
  }
  if (filters.modulistica_id) {
    const modLabel = modulisticaOptions.find((m) => String(m.id) === String(filters.modulistica_id))?.label || filters.modulistica_id;
    activeFilterChips.push({
      key: 'modulistica_id',
      label: `Modello: ${modLabel}`,
      onDelete: () => {
        setFilters((prev) => ({ ...prev, modulistica_id: '' }));
        setPage(0);
      }
    });
  }
  if (filters.date_from || filters.date_to) {
    const from = filters.date_from ? formatDateIt(filters.date_from) : '';
    const to = filters.date_to ? formatDateIt(filters.date_to) : '';
    activeFilterChips.push({
      key: 'date_range',
      label: `Data programmata: ${[from, to].filter(Boolean).join(' → ')}`,
      onDelete: () => {
        setFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));
        setPage(0);
      }
    });
  }
  if (filters.inviato_from || filters.inviato_to) {
    const from = filters.inviato_from ? formatDateIt(filters.inviato_from) : '';
    const to = filters.inviato_to ? formatDateIt(filters.inviato_to) : '';
    activeFilterChips.push({
      key: 'inviato_range',
      label: `Inviato: ${[from, to].filter(Boolean).join(' → ')}`,
      onDelete: () => {
        setFilters((prev) => ({ ...prev, inviato_from: '', inviato_to: '' }));
        setPage(0);
      }
    });
  }
  if (Number(legalStaleDays) !== 30) {
    activeFilterChips.push({
      key: 'legal_stale_days',
      label: `Soglia legale: ${legalStaleDays}gg`,
      onDelete: () => {
        setLegalStaleDays(30);
        setPage(0);
      }
    });
  }
  if (onlyLegalStale) {
    activeFilterChips.push({
      key: 'only_legal_stale',
      label: 'Solo da scartare',
      onDelete: () => {
        setOnlyLegalStale(false);
        setPage(0);
      }
    });
  }

  useEffect(() => {
    if (!openDialog) return;
    const id = Number(formData.cliente_record_id);
    if (!Number.isInteger(id) || id < 1) {
      setCustomerSelected(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/invii-modulistica/customers/by-id/${id}`);
        if (cancelled) return;
        const c = res.data || null;
        if (!c?.id) return;
        setCustomerSelected({
          id: String(c.id),
          label: c.full_name || `${c.surname || ''} ${c.name || ''}`.trim() || `Cliente ${c.id}`,
          raw: c
        });
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [openDialog, formData.cliente_record_id]);

  useEffect(() => {
    if (!openDialog) return;
    const term = String(customerInput || '').trim();
    if (term.length < 2) {
      setCustomerOptions([]);
      setCustomerLoading(false);
      return;
    }
    let cancelled = false;
    setCustomerLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/invii-modulistica/customers', { params: { search: term, limit: 20 } });
        if (cancelled) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setCustomerOptions(
          list.map((c) => ({
            id: String(c.id),
            label: c.full_name || `${c.surname || ''} ${c.name || ''}`.trim() || `Cliente ${c.id}`,
            raw: c
          }))
        );
      } catch (e) {
        if (!cancelled) setCustomerOptions([]);
      } finally {
        if (!cancelled) setCustomerLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [openDialog, customerInput]);

  const createSortHandler = (property) => () => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrent(row);
      setFormData({
        tipo_canale: String(row.tipo_canale || 'email'),
        stato: String(row.stato || 'da_inviare'),
        cliente_record_id: row.cliente_record_id ? String(row.cliente_record_id) : '',
        cognome: row.cognome || '',
        nome: row.nome || '',
        codice_fiscale: row.codice_fiscale || '',
        partita_iva: row.partita_iva || '',
        email: row.email || '',
        telefono: row.telefono || '',
        modulistica_id: row.modulistica_id ? String(row.modulistica_id) : '',
        modello: row.modello || '',
        data_programmata_invio: String(row.data_programmata_invio || '').slice(0, 10),
        data_scadenza: String(row.data_scadenza || '').slice(0, 10)
      });
    } else {
      setCurrent(null);
      const { to } = getCurrentMonthFromToTodayYmd();
      setFormData({ ...defaultForm, data_programmata_invio: to, data_scadenza: to });
    }
    setError('');
    setOpenDialog(true);
    setCustomerInput('');
    setCustomerOptions([]);
    setPreviewHtml(false);
    setDialogTab(0);
  };

  const handleCloseDialog = () => {
    if (saveLoading) return;
    setOpenDialog(false);
    setCurrent(null);
    setError('');
    setCustomerInput('');
    setCustomerOptions([]);
    setCustomerLoading(false);
    setCustomerSelected(null);
    setPreviewHtml(false);
    setDialogTab(0);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSaveLoading(true);
    try {
      const payload = {
        ...formData,
        cliente_record_id: formData.cliente_record_id ? Number(formData.cliente_record_id) : null,
        modulistica_id: formData.modulistica_id ? Number(formData.modulistica_id) : null,
        data_programmata_invio: formData.data_programmata_invio || null,
        data_scadenza: formData.data_scadenza || null
      };

      if (current?.id) {
        await api.put(`/invii-modulistica/${current.id}`, payload);
        toast.success('Invio aggiornato');
      } else {
        await api.post('/invii-modulistica', payload);
        toast.success('Invio creato');
      }

      await fetchRows();
      handleCloseDialog();
    } catch (e) {
      console.error('Error saving invio modulistica:', e);
      const message = e.response?.data?.message || 'Errore nel salvataggio';
      setError(message);
      toast.error(message);
    } finally {
      setSaveLoading(false);
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/invii-modulistica/${deleteTarget.id}`);
      toast.success('Invio eliminato');
      closeDeleteDialog();
      await fetchRows();
    } catch (e) {
      console.error('Error deleting invio modulistica:', e);
      toast.error('Errore durante l’eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const programmati = Number(stats?.programmati_count || 0);
  const daInviare = Number(stats?.da_inviare_count || 0);
  const inviati = Number(stats?.inviati_count || 0);
  const clientiRaggiunti = Number(stats?.clienti_raggiunti_count || 0);
  const tassoInvio = Number(stats?.tasso_invio_percentuale || 0);

  const prev = stats?.prev_period || null;
  const buildSubtitle = (deltaAbs, deltaPct) => {
    const dA = Number(deltaAbs || 0);
    const isNeg = dA < 0;
    const sign = dA > 0 ? '+' : '';
    const pct = deltaPct === null || deltaPct === undefined ? '—' : `${sign}${formatPercent1(deltaPct)}`;
    return {
      text: `${sign}${formatNumber(dA)} (${pct}%) vs periodo precedente`,
      color: isNeg ? '#ef4444' : '#22c55e'
    };
  };

  const programmatiSub = prev ? buildSubtitle(prev.delta_programmati_abs, prev.delta_programmati_pct) : null;
  const daInviareSub = prev ? buildSubtitle(prev.delta_da_inviare_abs, prev.delta_da_inviare_pct) : null;
  const inviatiSub = prev ? buildSubtitle(prev.delta_inviati_abs, prev.delta_inviati_pct) : null;
  const clientiSub = prev ? buildSubtitle(prev.delta_clienti_abs, prev.delta_clienti_pct) : null;

  const tassoSub = programmati > 0 ? { text: 'inviati / programmati', color: 'text.secondary' } : null;

  const activeBulkEmailJobsCount = useMemo(
    () => (jobs || []).filter((j) => j?.type === 'invii_modulistica_bulk_send_email' && j?.status === 'active').length,
    [jobs]
  );

  const activeBulkEmailProgress = useMemo(() => {
    const active = (jobs || []).filter((j) => j?.type === 'invii_modulistica_bulk_send_email' && j?.status === 'active');
    const values = active
      .map((j) => Number(j?.progress))
      .filter((v) => Number.isFinite(v))
      .map((v) => Math.max(0, Math.min(100, v)));
    if (!values.length) return null;
    return Math.max(...values);
  }, [jobs]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Mail size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Invio Promemoria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione invii promemoria (email / whatsapp / pec) tramite modelli
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshCw size={18} />} onClick={fetchRows} disabled={loading}>
            Aggiorna
          </Button>
          <Button variant="outlined" startIcon={<Upload size={18} />} onClick={handleOpenCsvDialog} disabled={loading}>
            Carica CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<MoreVertical size={18} />}
            onClick={openBulkMenu}
            disabled={loading || (total === 0 && selectedIds.length === 0)}
          >
            Azioni{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </Button>
          <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => handleOpenDialog(null)} disabled={loading}>
            Nuovo
          </Button>
        </Box>
      </Box>

      <Menu anchorEl={bulkMenuAnchorEl} open={bulkMenuOpen} onClose={closeBulkMenu}>
        <MenuItem onClick={() => openBulkDialog('send_email')}>
          <ListItemIcon sx={{ color: '#f59e0b' }}>
            <Mail size={18} />
          </ListItemIcon>
          <ListItemText primary="Invia Email" />
        </MenuItem>
        <MenuItem onClick={() => openBulkDialog('change_status')}>
          <ListItemIcon sx={{ color: '#f59e0b' }}>
            <RefreshCw size={18} />
          </ListItemIcon>
          <ListItemText primary="Cambia stato" />
        </MenuItem>
        <MenuItem onClick={() => openBulkDialog('delete')}>
          <ListItemIcon sx={{ color: '#ef4444' }}>
            <DeleteIcon size={18} />
          </ListItemIcon>
          <ListItemText primary="Cancella" />
        </MenuItem>
      </Menu>

      {activeBulkEmailJobsCount > 0 ? (
        <Alert
          severity="info"
          variant="outlined"
          icon={<CircularProgress size={16} />}
          sx={{ mb: 2, py: 0.75, alignItems: 'center' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Invio email in corso
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Puoi continuare a lavorare: l’esito apparirà nelle notifiche (campanella).
          </Typography>
          <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <LinearProgress
                variant={activeBulkEmailProgress === null ? 'indeterminate' : 'determinate'}
                value={activeBulkEmailProgress === null ? undefined : activeBulkEmailProgress}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: 'right' }}>
              {activeBulkEmailProgress === null ? '' : `${Math.round(activeBulkEmailProgress)}%`}
            </Typography>
          </Box>
        </Alert>
      ) : null}

      {stats && (
        <Box
          sx={{
            mb: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))'
            }
          }}
        >
          <StatWidget
            title="Programmati (Periodo)"
            value={formatNumber(programmati)}
            icon={Mail}
            color="#3b82f6"
            subtitle={programmatiSub ? programmatiSub.text : 'Nessun dato precedente'}
            subtitleColor={programmatiSub ? programmatiSub.color : 'text.secondary'}
          />
          <StatWidget
            title="Da inviare (Periodo)"
            value={formatNumber(daInviare)}
            icon={AlertCircle}
            color="#f59e0b"
            subtitle={daInviareSub ? daInviareSub.text : 'Nessun dato precedente'}
            subtitleColor={daInviareSub ? daInviareSub.color : 'text.secondary'}
            onClick={() => {
              setFilters((prev) => ({ ...prev, stato: prev.stato === 'da_inviare' ? '' : 'da_inviare' }));
              setPage(0);
            }}
          />
          <StatWidget
            title="Inviati (Periodo)"
            value={formatNumber(inviati)}
            icon={Mail}
            color="#22c55e"
            subtitle={inviatiSub ? inviatiSub.text : 'Nessun dato precedente'}
            subtitleColor={inviatiSub ? inviatiSub.color : 'text.secondary'}
          />
          <StatWidget
            title="Clienti Raggiunti (Periodo)"
            value={formatNumber(clientiRaggiunti)}
            icon={Mail}
            color="#a855f7"
            subtitle={clientiSub ? clientiSub.text : 'Nessun dato precedente'}
            subtitleColor={clientiSub ? clientiSub.color : 'text.secondary'}
          />
          <StatWidget
            title="Tasso Invio"
            value={`${formatPercent1(tassoInvio)}%`}
            icon={Mail}
            color="#4f46e5"
            subtitle={tassoSub ? tassoSub.text : ''}
            subtitleColor={tassoSub ? tassoSub.color : 'text.secondary'}
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <StyledCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'nowrap' }}>
            <TextField
              placeholder="Cerca per cliente, CF, email, modello..."
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={20} />
                  </InputAdornment>
                )
              }}
              sx={{ flex: 1 }}
            />
            <Tooltip title="Gestione filtri">
              <IconButton
                size="small"
                color={activeFilterChips.length > 0 ? 'primary' : 'default'}
                onClick={() => setFiltersPanelOpen((prev) => !prev)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {activeFilterChips.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, mr: 0.5 }}>
                Filtri attivi
              </Typography>
              {activeFilterChips.map((c) => (
                <Chip
                  key={c.key}
                  size="small"
                  label={c.label}
                  onDelete={c.onDelete}
                  sx={{ fontSize: 12 }}
                />
              ))}
            </Box>
          ) : null}

          <Collapse in={filtersPanelOpen}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={filters.stato}
                    label="Stato"
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, stato: String(e.target.value) }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    <MenuItem value="da_inviare">Da inviare</MenuItem>
                    <MenuItem value="inviato">Inviato</MenuItem>
                    <MenuItem value="errore_invio">Errore invio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Canale</InputLabel>
                  <Select
                    value={filters.tipo_canale}
                    label="Canale"
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, tipo_canale: String(e.target.value) }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    <MenuItem value="pec">PEC</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Utente</InputLabel>
                  <Select
                    value={filters.creato_da_utente || ''}
                    label="Utente"
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, creato_da_utente: String(e.target.value) }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {creatoriOptions.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  size="small"
                  options={modulisticaOptions}
                  value={selectedModulisticaFilter}
                  onChange={(_event, newValue) => {
                    setFilters((prev) => ({ ...prev, modulistica_id: newValue?.id ? String(newValue.id) : '' }));
                    setPage(0);
                  }}
                  getOptionLabel={(opt) => opt?.label || ''}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  renderInput={(params) => <TextField {...params} label="Modello" />}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    label="Data programmata (Da)"
                    value={filters.date_from}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, date_from: e.target.value }));
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  {filters.date_from ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, date_from: '' }));
                        setPage(0);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    label="Data programmata (A)"
                    value={filters.date_to}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, date_to: e.target.value }));
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  {filters.date_to ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, date_to: '' }));
                        setPage(0);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    label="Inviato il (Da)"
                    value={filters.inviato_from || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, inviato_from: e.target.value }));
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  {filters.inviato_from ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, inviato_from: '' }));
                        setPage(0);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    label="Inviato il (A)"
                    value={filters.inviato_to || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, inviato_to: e.target.value }));
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  {filters.inviato_to ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, inviato_to: '' }));
                        setPage(0);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={onlyLegalStale}
                      onChange={(e) => {
                        setOnlyLegalStale(Boolean(e.target.checked));
                        setPage(0);
                      }}
                      size="small"
                    />
                  }
                  label="Solo da scartare"
                  sx={{ mt: 0.25 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  label="Soglia legale (gg)"
                  value={legalStaleDays}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isFinite(n) || n < 0) return;
                    setLegalStaleDays(Math.floor(n));
                    setPage(0);
                  }}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
            </Grid>
          </Collapse>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={rows.length > 0 && rows.every((r) => selectedIdsSet.has(Number(r.id)))}
                      indeterminate={
                        rows.some((r) => selectedIdsSet.has(Number(r.id))) &&
                        !rows.every((r) => selectedIdsSet.has(Number(r.id)))
                      }
                      onChange={toggleAllVisible}
                      inputProps={{ 'aria-label': 'Seleziona tutti' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'inviato_at'}
                      direction={orderBy === 'inviato_at' ? orderDirection : 'asc'}
                      onClick={createSortHandler('inviato_at')}
                    >
                      Inviato il
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_programmata_invio'}
                      direction={orderBy === 'data_programmata_invio' ? orderDirection : 'asc'}
                      onClick={createSortHandler('data_programmata_invio')}
                    >
                      Data programmata
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'tipo_canale'}
                      direction={orderBy === 'tipo_canale' ? orderDirection : 'asc'}
                      onClick={createSortHandler('tipo_canale')}
                    >
                      Canale
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'cliente'}
                      direction={orderBy === 'cliente' ? orderDirection : 'asc'}
                      onClick={createSortHandler('cliente')}
                    >
                      Cliente
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Ult. nota legale</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Ult. nota contabile</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'modulistica_nome'}
                      direction={orderBy === 'modulistica_nome' ? orderDirection : 'asc'}
                      onClick={createSortHandler('modulistica_nome')}
                    >
                      Modello
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'stato'}
                      direction={orderBy === 'stato' ? orderDirection : 'asc'}
                      onClick={createSortHandler('stato')}
                    >
                      Stato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Motivo errore</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_scadenza'}
                      direction={orderBy === 'data_scadenza' ? orderDirection : 'asc'}
                      onClick={createSortHandler('data_scadenza')}
                    >
                      Scadenza
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Invio</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                      Nessun invio trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const threshold = Number(legalStaleDays);
                    const days = diffDaysFromToday(row?.last_legal_note_date);
                    const isLegalStale =
                      Number.isFinite(threshold) && threshold >= 0 ? (days == null ? true : days > threshold) : false;

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={isLegalStale ? { backgroundColor: 'rgba(239, 68, 68, 0.06)' } : undefined}
                      >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIdsSet.has(Number(row.id))}
                          onChange={() => toggleSelectedId(row.id)}
                          inputProps={{ 'aria-label': `Seleziona invio ${row.id}` }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'grid', justifyItems: 'start', gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.stato === 'inviato' ? formatDateTimeIt(row.inviato_at) : '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {row.stato === 'inviato' ? (row.creato_da_nome || '-') : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateIt(row.data_programmata_invio)}</TableCell>
                      <TableCell>
                        <CanaleChip value={row.tipo_canale} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} color={isLegalStale ? 'error.main' : 'text.primary'}>
                          {[row.cognome, row.nome].filter(Boolean).join(' ') || '-'}
                        </Typography>
                        {row.email ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Mail size={16} color="#4f46e5" />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {row.email}
                            </Typography>
                          </Box>
                        ) : null}
                        {row.telefono ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Phone size={16} color="#22c55e" />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {row.telefono}
                            </Typography>
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'grid', justifyItems: 'start', gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} color={isLegalStale ? 'error.main' : 'text.primary'}>
                            {formatDateIt(row.last_legal_note_date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {row.last_legal_ref_name || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'grid', justifyItems: 'start', gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateIt(row.last_accounting_note_date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {row.last_accounting_ref_name || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.modulistica_nome || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'grid', justifyItems: 'center', gap: 0.35 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, minWidth: 0 }}>
                            <Tooltip title={row.email_mittente ? `Da: ${row.email_mittente}` : ''} disableHoverListener={!row.email_mittente}>
                              <Box component="span">
                                <StatoChip value={row.stato} />
                              </Box>
                            </Tooltip>
                            {row.email_mittente ? (
                              <Tooltip title={`Da: ${row.email_mittente}`}>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}>
                                  <HelpCircle size={16} />
                                </Box>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.stato === 'errore_invio' ? (
                          <Tooltip title={row.errore_invio_dettaglio ? String(row.errore_invio_dettaglio) : ''} disableHoverListener={!row.errore_invio_dettaglio}>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 240 }}>
                              {errorReasonLabel(row) || '-'}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateIt(row.data_scadenza)}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          const decision = getInvioEmailDecision(row, legalStaleDays, !bulkApplyLegalThreshold);
                          if (decision.status === 'send') {
                            return (
                              <Tooltip title={decision.label}>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <CheckCircle2 size={18} color="#16a34a" />
                                </Box>
                              </Tooltip>
                            );
                          }
                          if (decision.status === 'blocked') {
                            return (
                              <Tooltip title={decision.label}>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <AlertCircle size={18} color="#ef4444" />
                                </Box>
                              </Tooltip>
                            );
                          }
                          return (
                            <Tooltip title={decision.label}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Ban size={18} color="#94a3b8" />
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <IconButton size="small" color="primary" title="Modifica" onClick={() => handleOpenDialog(row)}>
                            <EditIcon size={18} />
                          </IconButton>
                          <IconButton size="small" color="error" title="Elimina" onClick={() => openDeleteDialog(row)}>
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{current ? 'Modifica invio' : 'Nuovo invio'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs
            value={dialogTab}
            onChange={(_event, newValue) => setDialogTab(Number(newValue))}
            sx={{ mb: 2 }}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab label="Dati" value={0} />
            <Tab label="Contenuto" value={1} />
            <Tab label="Placeholder" value={2} />
          </Tabs>

          {dialogTab === 0 ? (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Canale</InputLabel>
                    <Select name="tipo_canale" label="Canale" value={formData.tipo_canale} onChange={handleFormChange}>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="pec">PEC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Stato</InputLabel>
                    <Select name="stato" label="Stato" value={formData.stato} onChange={handleFormChange}>
                      <MenuItem value="da_inviare">Da inviare</MenuItem>
                      <MenuItem value="inviato">Inviato</MenuItem>
                      <MenuItem value="errore_invio">Errore invio</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={modulisticaOptions}
                    value={selectedModulisticaForm}
                    onChange={async (_event, newValue) => {
                      const id = newValue?.id != null ? Number(newValue.id) : null;
                      if (!Number.isInteger(id) || id <= 0) {
                        setFormData((prev) => ({ ...prev, modulistica_id: '' }));
                        return;
                      }
                      setFormData((prev) => ({ ...prev, modulistica_id: String(id) }));
                      try {
                        setModulisticaContentLoading(true);
                        const res = await api.get(`/modulistica/${id}`);
                        const data = res?.data || null;
                        const content = data?.content != null ? String(data.content) : '';
                        setFormData((prev) => ({ ...prev, modulistica_id: String(id), modello: content }));
                      } catch (e) {
                        toast.error('Impossibile caricare il contenuto del modello');
                      } finally {
                        setModulisticaContentLoading(false);
                      }
                    }}
                    getOptionLabel={(opt) => opt?.label || ''}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    disabled={modulisticaContentLoading || saveLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Modello"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {modulisticaContentLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    options={customerOptions}
                    value={customerSelected}
                    loading={customerLoading}
                    onInputChange={(_event, value) => setCustomerInput(value)}
                    onChange={(_event, newValue) => {
                      setCustomerSelected(newValue || null);
                      const c = newValue?.raw || null;
                      setFormData((prev) => ({
                        ...prev,
                        cliente_record_id: newValue?.id ? String(newValue.id) : '',
                        cognome: c ? (c.surname || '') : prev.cognome,
                        nome: c ? (c.name || '') : prev.nome,
                        codice_fiscale: c ? (c.codice_fiscale || '') : prev.codice_fiscale,
                        partita_iva: c ? (c.partita_iva || '') : prev.partita_iva,
                        email: c ? (c.email || '') : prev.email,
                        telefono: c ? (c.telephone || '') : prev.telefono
                      }));
                    }}
                    getOptionLabel={(opt) => opt?.label || ''}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente (tabella customer)"
                        placeholder="Cerca cliente per nome, email o telefono..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {customerLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField name="cognome" label="Cognome" value={formData.cognome} onChange={handleFormChange} fullWidth />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField name="nome" label="Nome" value={formData.nome} onChange={handleFormChange} fullWidth />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="codice_fiscale"
                    label="Codice fiscale"
                    value={formData.codice_fiscale}
                    onChange={handleFormChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="partita_iva"
                    label="Partita IVA"
                    value={formData.partita_iva}
                    onChange={handleFormChange}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="Email destinatario"
                    value={formData.email}
                    onChange={handleFormChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="telefono"
                    label="Telefono destinatario"
                    value={formData.telefono}
                    onChange={handleFormChange}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="data_programmata_invio"
                    label="Data programmata invio"
                    type="date"
                    value={formData.data_programmata_invio}
                    onChange={handleFormChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="data_scadenza"
                    label="Data scadenza"
                    type="date"
                    value={formData.data_scadenza}
                    onChange={handleFormChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : dialogTab === 1 ? (
            <Box sx={{ mt: 1 }}>
              {!selectedModulisticaForm ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Seleziona prima un modello nel tab “Dati” per visualizzare e modificare il contenuto.
                </Alert>
              ) : null}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Contenuto (modello renderizzato)
                    </Typography>
                  </Box>
                  {selectedModulisticaForm ? (
                    <Typography variant="caption" color="text.secondary">
                      Modello: {selectedModulisticaForm.label}
                    </Typography>
                  ) : null}
                </Box>
                <FormControl size="small">
                  <Select
                    value={previewHtml ? 'preview' : 'edit'}
                    onChange={(e) => setPreviewHtml(String(e.target.value) === 'preview')}
                    size="small"
                  >
                    <MenuItem value="edit">Modifica</MenuItem>
                    <MenuItem value="preview">Anteprima</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {!previewHtml ? (
                <Box
                  sx={{
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '& .ql-toolbar': { border: 'none', borderBottom: '1px solid rgba(0,0,0,0.12)' },
                    '& .ql-container': { border: 'none', minHeight: 420 },
                    '& .ql-editor': { minHeight: 420 }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={String(formData.modello || '')}
                    onChange={(value) => setFormData((prev) => ({ ...prev, modello: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                </Box>
              ) : (
                <Box
                  component="iframe"
                  title="Anteprima contenuto"
                  sandbox=""
                  srcDoc={String(formData.modello || '')}
                  style={{
                    width: '100%',
                    height: '420px',
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 8,
                    background: '#fff'
                  }}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                I placeholder vengono risolti al momento dell’invio (non nella preview dell’editor).
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { token: '{cliente}', desc: 'nome cliente (da customer)' },
                  { token: '{scadenza}', desc: 'data scadenza (gg/mm/aaaa)' },
                  { token: '{data_nascita}', desc: 'data nascita (da customer)' },
                  { token: '{luogo_nascita}', desc: 'luogo nascita (da customer)' },
                  { token: '{luogo_residenza}', desc: 'luogo residenza (da customer)' },
                  { token: '{codice_fiscale}', desc: 'codice fiscale (da customer)' },
                  { token: '{email}', desc: 'email (da customer)' },
                  { token: '{telefono}', desc: 'telefono (da customer)' },
                  { token: '{data_corrente}', desc: 'data corrente (gg/mm/aaaa)' },
                  { token: '{rif_utente}', desc: 'valore da CSV (se presente)' },
                  { token: '{rif_contatto}', desc: 'valore da CSV (se presente)' }
                ].map((x) => (
                  <Box key={x.token} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={x.token}
                      sx={{ fontFamily: 'monospace', fontWeight: 900 }}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {x.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDialog} disabled={saveLoading}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saveLoading}>
            {saveLoading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Eliminare invio {formatDateIt(deleteTarget?.data_programmata_invio)} — {[deleteTarget?.cognome, deleteTarget?.nome].filter(Boolean).join(' ') || '-'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={closeBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkAction === 'send_email'
            ? 'Invia Email'
            : bulkAction === 'change_status'
              ? 'Cambia stato'
              : bulkAction === 'delete'
                ? 'Cancella'
                : 'Azione'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <RadioGroup
              row
              value={bulkScope}
              onChange={(e) => setBulkScope(String(e.target.value || 'selected'))}
            >
              <FormControlLabel
                value="selected"
                control={<Radio />}
                disabled={selectedIds.length === 0}
                label={`Selezionati (${selectedIds.length})`}
              />
              <FormControlLabel value="all" control={<Radio />} label={`Tutti i risultati (${total})`} disabled={total === 0} />
            </RadioGroup>
          </FormControl>

          {bulkAction === 'send_email' && (
            <>
              <Autocomplete
                sx={{ mt: 2 }}
                size="small"
                options={emailAccountOptions}
                value={emailAccountOptions.find((a) => String(a.id) === String(bulkEmailAccountId || '')) || null}
                onChange={(_event, newValue) => setBulkEmailAccountId(newValue?.id ? String(newValue.id) : '')}
                getOptionLabel={(opt) => opt?.label || ''}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderInput={(params) => <TextField {...params} label="Account email mittente *" />}
              />

              <TextField
                sx={{ mt: 2 }}
                label="Oggetto *"
                value={bulkEmailSubject}
                onChange={(e) => setBulkEmailSubject(e.target.value)}
                fullWidth
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={bulkApplyLegalThreshold}
                    onChange={(e) => setBulkApplyLegalThreshold(Boolean(e.target.checked))}
                  />
                }
                label={`Considero la soglia gg Legale? ${bulkApplyLegalThreshold ? 'Sì' : 'No'}`}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Typography variant="body2">
                    L’azione invia email ai record del canale “email” con stato “da inviare”. I record non compatibili vengono scartati.
                  </Typography>
                  {bulkApplyLegalThreshold ? (
                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'error.main' }}>
                      Di tutti quei Clienti (evidenziati in rosso), verranno scartati i Clienti con ultimo aggiornamento del legale oltre {Math.floor(Number(legalStaleDays) || 0)} giorni (o assente).
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                      Soglia legale NON considerata: nessuno scarto basato sui giorni dall’ultimo aggiornamento del legale.
                    </Typography>
                  )}
                </Box>
              </Alert>
            </>
          )}

          {bulkAction === 'change_status' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="bulk-status-label">Nuovo stato</InputLabel>
              <Select
                labelId="bulk-status-label"
                label="Nuovo stato"
                value={bulkStatus}
                onChange={(e) => setBulkStatus(String(e.target.value || 'da_inviare'))}
              >
                {(meta.stati?.length ? meta.stati : ['da_inviare', 'inviato', 'errore_invio']).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {bulkAction === 'delete' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Questa operazione elimina definitivamente i record selezionati (o tutti i risultati filtrati).
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeBulkDialog} disabled={bulkLoading}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color={bulkAction === 'delete' ? 'error' : 'primary'}
            onClick={runBulkAction}
            disabled={
              bulkLoading ||
              (bulkScope === 'selected' && selectedIds.length === 0) ||
              (bulkScope === 'all' && total === 0) ||
              (bulkAction === 'send_email' && (!String(bulkEmailAccountId || '').trim() || !String(bulkEmailSubject || '').trim()))
            }
          >
            {bulkLoading ? 'Operazione...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkEmailResultOpen} onClose={() => setBulkEmailResultOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Esito invio email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 1.5, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" color="success" label={`Inviate: ${Number(bulkEmailResult?.sent || 0).toLocaleString('it-IT')}`} />
              <Chip size="small" color="error" label={`Errori: ${Number(bulkEmailResult?.failed || 0).toLocaleString('it-IT')}`} />
              <Chip size="small" label={`Totale: ${Number(bulkEmailResult?.total || 0).toLocaleString('it-IT')}`} />
              <Chip size="small" variant="outlined" label={`Mittente: ${bulkEmailResult?.email_mittente || '-'}`} />
            </Box>

            {bulkEmailResult?.fail_reasons ? (
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Cause principali
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email non valida: {Number(bulkEmailResult.fail_reasons.invalid_email || 0).toLocaleString('it-IT')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contenuto vuoto: {Number(bulkEmailResult.fail_reasons.empty_content || 0).toLocaleString('it-IT')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Errore invio (SMTP/provider): {Number(bulkEmailResult.fail_reasons.send_error || 0).toLocaleString('it-IT')}
                </Typography>
              </Box>
            ) : null}

            {Array.isArray(bulkEmailResult?.errors) && bulkEmailResult.errors.length ? (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Esempi errori (campione)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 90, fontWeight: 800 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Messaggio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkEmailResult.errors.map((e) => (
                      <TableRow key={String(e?.id || Math.random())}>
                        <TableCell>{e?.id}</TableCell>
                        <TableCell>{e?.message || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={downloadBulkEmailErrorsCsv}
            disabled={!Array.isArray(bulkEmailResult?.errors) || bulkEmailResult.errors.length === 0}
          >
            Scarica errori CSV
          </Button>
          <Button variant="contained" onClick={() => setBulkEmailResultOpen(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={csvInfoOpen} onClose={handleCloseCsvInfoDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Upload size={20} color="#4f46e5" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Carica CSV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import massivo invii promemoria da file CSV
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Il CSV deve contenere i campi: codice fiscale, nome modello, data invio, data scadenza, rif_utente, rif_contatto.
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <FileUp size={20} color="#2563eb" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Selezione file
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Il sistema legge le righe e identifica il modello tramite “nome modello” (tabella modulisticas).
                </Typography>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <CheckCircle2 size={20} color="#16a34a" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Validazione
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Validazione delle date e recupero anagrafica dalla tabella customer (second_db) partendo dal codice fiscale (o rif_contatto come fallback).
                </Typography>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <ShieldCheck size={20} color="#7c3aed" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Import controllato
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vengono creati record in invii_modulistica con stato “da inviare” e rif_utente uguale all’utente che sta caricando il CSV.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseCsvInfoDialog}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleContinueCsvInfo}>
            Continua
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={csvUploadOpen} onClose={handleCloseCsvUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Upload size={20} color="#4f46e5" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Import CSV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seleziona file e parametri di import
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvFileChange} />

          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                File CSV
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {csvFile ? csvFile.name : 'Nessun file selezionato'}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={handleCsvPick} disabled={csvImportLoading}>
              Seleziona file
            </Button>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Canale di default</InputLabel>
                <Select
                  value={csvDefaultChannel}
                  label="Canale di default"
                  onChange={(e) => setCsvDefaultChannel(String(e.target.value))}
                  disabled={csvImportLoading}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="pec">PEC</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={modulisticaOptions}
                value={csvDefaultModel}
                onChange={(_event, newValue) => setCsvDefaultModel(newValue || null)}
                getOptionLabel={(opt) => opt?.label || ''}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Modello di default *"
                    placeholder="Seleziona un'opzione"
                    required
                    error={!csvDefaultModel?.id}
                  />
                )}
                disabled={csvImportLoading}
              />
            </Grid>
          </Grid>

          {csvImportResult ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              {csvImportResult.message || 'Import completato'}
            </Alert>
          ) : null}

          {csvImportErrors.length ? (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Righe scartate
              </Typography>
              <Box sx={{ maxHeight: 180, overflow: 'auto', display: 'grid', gap: 0.5 }}>
                {csvImportErrors.map((err, idx) => (
                  <Typography key={`${err.row || 'r'}-${idx}`} variant="caption" color="text.secondary">
                    Riga {err.row}: {err.message}
                  </Typography>
                ))}
              </Box>
            </Paper>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseCsvUploadDialog} disabled={csvImportLoading}>
            Chiudi
          </Button>
          <Button variant="contained" onClick={handleCsvImport} disabled={csvImportLoading || !csvFile || !csvDefaultModel?.id}>
            {csvImportLoading ? 'Importazione...' : 'Importa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
